import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Slide, Button } from '@mui/material';
import { CheckCircle, Error, Info, Warning } from '@mui/icons-material';

const ToastNotification = ({ 
  open, 
  type = 'success', 
  title, 
  message, 
  duration = 3000, 
  onClose,
  showOkButton = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progressWidth, setProgressWidth] = useState(100);
  const timerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref updated without resetting timer
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setProgressWidth(100);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setTimeout(() => {
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    }, 300); // Wait for exit animation
  }, []);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setProgressWidth(100);
      
      // Clear any existing timers first
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      // Animate progress bar from 100% to 0%
      progressIntervalRef.current = setInterval(() => {
        setProgressWidth(prev => {
          const newWidth = prev - (100 / (duration / 50)); // Update every 50ms
          return newWidth <= 0 ? 0 : newWidth;
        });
      }, 50);
      
      timerRef.current = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      };
    } else {
      setProgressWidth(100);
    }
    // Only depend on open and duration - handleClose is stable and doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, duration]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle sx={{ color: 'white', fontSize: 24 }} />;
      case 'error':
        return <Error sx={{ color: 'white', fontSize: 24 }} />;
      case 'info':
        return <Info sx={{ color: 'white', fontSize: 24 }} />;
      case 'warning':
      case 'neutral':
        return <Warning sx={{ color: 'white', fontSize: 24 }} />;
      default:
        return <CheckCircle sx={{ color: 'white', fontSize: 24 }} />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'linear-gradient(135deg, #4caf50, #45a049)';
      case 'error':
        return 'linear-gradient(135deg, #f44336, #d32f2f)';
      case 'info':
        return 'linear-gradient(135deg, #2196f3, #1976d2)';
      case 'warning':
      case 'neutral':
        return 'linear-gradient(135deg, #757575, #616161)'; // Grey neutral color
      default:
        return 'linear-gradient(135deg, #4caf50, #45a049)';
    }
  };

  if (!open) return null;

  return (
    <Slide direction="left" in={isVisible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          minWidth: 300,
          maxWidth: 400,
          background: getBackgroundColor(),
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          p: 2,
          pb: 2.5,
          overflow: 'hidden',
          transform: 'translateX(0)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateX(-5px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          },
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            '0%': {
              transform: 'translateX(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {getIcon()}
          <Box sx={{ flex: 1, color: 'white' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white !important' }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, color: 'white !important', mb: showOkButton ? 1.5 : 0 }}>
              {message}
            </Typography>
            {showOkButton && (
              <Button
                variant="contained"
                size="small"
                onClick={handleClose}
                sx={{
                  mt: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 60,
                }}
              >
                OK
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Progress bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              background: 'rgba(255,255,255,0.9)',
              width: `${progressWidth}%`,
              transition: 'width 50ms linear',
              borderRadius: progressWidth >= 100 ? '0 0 8px 8px' : '0 0 0 8px',
            }}
          />
        </Box>
      </Box>
    </Slide>
  );
};

export default ToastNotification; 